import { getAggregateError, } from "@bracket/catch";
import type { IBracketStore, LoadedVaultWithPnl, ManagedVault, } from "@types";
import { setVaultVersions, } from "@utils";
import { gql, } from "./gql";
import { getLoadedVault, } from "./loaded";
import { getManagedVault, } from "./mgmt";




/**
 * Retrieves both available and managed vaults for the current wallet
 * 
 * @returns Promise resolving to an object containing:
 *   - available: Array of LoadedVault objects representing vaults available to the user
 *   - providing: Array of ManagedVault objects representing vaults the user is providing liquidity to
 * 
 * @throws Error if:
 *   - No wallet address is set
 *   - Failed to obtain vaults from the graph
 *   - Failed to load vault data
 */
export const getVaults = async ( store: IBracketStore, ): Promise<{
	available: LoadedVaultWithPnl[];
	providing: ManagedVault[];
}> => {

	try {

		const address = store.wallet.address;
		const anon = address && import.meta.env.VITE_ANON_WALLET_ADDR.toLowerCase() === address.toLowerCase();
		const {
			wallet: {
				managed_vaults,
				vaults,
			},
		} = await gql<"VAULTS">("VAULTS", address as string);

		setVaultVersions([ ...vaults, ...managed_vaults, ]);

		const [
			available,
			providing,
		] = await Promise.all([
			Promise.all(vaults.map(v => getLoadedVault(store, {...v, state: "IDENTIFIABLE", }))),
			anon ? [] : Promise.all(managed_vaults.map(v => getManagedVault(store, {...v, state: "IDENTIFIABLE", }))),
		]);

		if(![vaults, managed_vaults].every(Array.isArray))
			throw new Error("failed obtaining vaults");

		return {
			available: available.sort((a, b) => a.symbol === "BBOR1" ? 1 : b.symbol === "BBOR1" ? -1 : 0),
			providing,
		};

	}
	catch(error) {

		throw getAggregateError(error, `vaults::getVaults has failed - ${error instanceof Error ? error.message : "unknown error"}`);
	}

};
